import { useEffect, useRef, useState } from 'react';

interface Conquista {
  icon: string;
  titulo: string;
  descricao: string;
  ano: string;
}

const conquistas: Conquista[] = [
  {
    icon: 'ri-trophy-line',
    titulo: '1ª Plataforma de Cartas do Futuro do Brasil',
    descricao: 'Pioneira em transformar a técnica de escrita terapêutica em uma plataforma digital completa',
    ano: '2021',
  },
  {
    icon: 'ri-brain-line',
    titulo: 'Certificação Internacional em PNL',
    descricao: 'Practitioner e Master em Programação Neurolinguística pela SBPNL',
    ano: '2019',
  },
  {
    icon: 'ri-heart-pulse-line',
    titulo: '30 Anos de Pânico Superados',
    descricao: 'Transformei minha própria história de sofrimento em liberdade e propósito',
    ano: '2020',
  },
  {
    icon: 'ri-global-line',
    titulo: '+12.847 Vidas Transformadas',
    descricao: 'Pessoas em 48 países já escreveram suas cartas e mudaram suas histórias',
    ano: '2024',
  },
  {
    icon: 'ri-book-open-line',
    titulo: 'Especialista em Transformação Pessoal',
    descricao: 'Mais de 15 técnicas de alta performance integradas em um método único',
    ano: '2022',
  },
  {
    icon: 'ri-star-line',
    titulo: 'Reconhecida por Influenciadores',
    descricao: 'Método validado e recomendado por especialistas em desenvolvimento pessoal',
    ano: '2023',
  },
];

const ConquistasReconhecimentos = () => {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Staggered reveal
          conquistas.forEach((_, index) => {
            setTimeout(() => {
              setVisibleCards((prev) => [...prev, index]);
            }, index * 150);
          });
        }
      },
      { threshold: 0.15 },
    );

    const current = sectionRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
      observer.disconnect();
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative py-32 bg-luxury-black overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-1/4 w-2 h-2 bg-gold-500/30 rounded-full animate-float" />
      <div
        className="absolute top-40 right-1/3 w-1.5 h-1.5 bg-gold-400/20 rounded-full animate-float"
        style={{ animationDelay: '2s' }}
      />
      <div
        className="absolute bottom-32 left-1/2 w-1 h-1 bg-gold-300/30 rounded-full animate-float"
        style={{ animationDelay: '3.5s' }}
      />

      <div className="container mx-auto px-6 lg:px-16 relative z-10">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-20">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-16 h-px bg-gradient-to-r from-transparent to-gold-500/50" />
            <span className="font-['Montserrat'] text-xs tracking-[0.3em] uppercase text-gold-400/70">
              Conquistas e Reconhecimentos
            </span>
            <div className="w-16 h-px bg-gradient-to-l from-transparent to-gold-500/50" />
          </div>

          <h2 className="font-['Playfair_Display'] text-4xl lg:text-6xl text-white font-light leading-tight mb-6">
            Uma jornada de{' '}
            <span className="italic text-gold-400">superação</span>
          </h2>

          <p className="font-['Montserrat'] text-sm lg:text-base text-white/45 leading-relaxed font-light tracking-wide">
            Cada conquista representa anos de dedicação, estudo e transformação pessoal. 
            Transformei minha dor em propósito e hoje ajudo milhares de pessoas a reescreverem suas histórias.
          </p>
        </div>

        {/* Grid de conquistas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {conquistas.map((conquista, index) => {
            const isVisible = visibleCards.includes(index);

            return (
              <div
                key={index}
                className="group relative transition-all duration-700 ease-out"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible
                    ? 'translateY(0) scale(1)'
                    : 'translateY(30px) scale(0.95)',
                  filter: isVisible ? 'blur(0px)' : 'blur(4px)',
                }}
              >
                {/* Card */}
                <div className="relative h-full bg-gradient-to-br from-luxury-charcoal/60 to-luxury-charcoal/40 backdrop-blur-sm border border-gold-500/10 rounded-2xl p-8 hover:border-gold-500/30 transition-all duration-500 hover:shadow-[0_8px_30px_rgba(201,168,76,0.15)] overflow-hidden">
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-gold-500/0 to-gold-500/0 group-hover:from-gold-500/5 group-hover:to-gold-500/10 transition-all duration-500 rounded-2xl" />

                  {/* Icon container */}
                  <div className="relative mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gold-500/20 to-gold-600/10 border border-gold-500/20 flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                      <i className={`${conquista.icon} text-3xl text-gold-400 w-8 h-8 flex items-center justify-center`} />
                    </div>
                    {/* Badge de ano */}
                    <div className="absolute -top-2 -right-2 bg-gold-500/20 border border-gold-500/30 rounded-full px-3 py-1">
                      <span className="font-['Montserrat'] text-[10px] text-gold-400 font-semibold tracking-wider">
                        {conquista.ano}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="font-['Playfair_Display'] text-xl text-white font-semibold mb-3 leading-tight">
                    {conquista.titulo}
                  </h3>

                  <p className="font-['Montserrat'] text-sm text-white/50 leading-relaxed font-light tracking-wide">
                    {conquista.descricao}
                  </p>

                  {/* Decorative corner */}
                  <div className="absolute bottom-0 right-0 w-20 h-20 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <div className="absolute bottom-0 right-0 w-full h-full border-b-2 border-r-2 border-gold-500/20 rounded-br-2xl" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom decorative line */}
        <div className="flex items-center justify-center gap-4 mt-20">
          <div className="w-24 h-px bg-gradient-to-r from-transparent to-gold-500/30" />
          <i className="ri-award-line text-gold-500/40 text-xl w-6 h-6 flex items-center justify-center" />
          <div className="w-24 h-px bg-gradient-to-l from-transparent to-gold-500/30" />
        </div>
      </div>
    </section>
  );
};

export default ConquistasReconhecimentos;